import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmDialog.css';
import './PromptDialog.css';

/**
 * SweetAlert tarzı metin girişi (prompt yerine).
 */
const PromptDialog = ({
  open,
  title = 'Giriş',
  message,
  placeholder,
  defaultValue = '',
  minLength = 0,
  multiline = true,
  confirmLabel = 'Tamam',
  cancelLabel = 'İptal',
  onCancel,
  onSubmit,
  nestedOverlay,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(defaultValue || '');
      setError('');
      setBusy(false);
    }
  }, [open, defaultValue]);

  const validate = useCallback(() => {
    const t = (value || '').trim();
    if (minLength > 0 && t.length < minLength) {
      setError(`En az ${minLength} karakter giriniz.`);
      return null;
    }
    if (!t && minLength === 0) {
      setError('Bu alan zorunludur.');
      return null;
    }
    setError('');
    return t;
  }, [value, minLength]);

  const handleSubmit = useCallback(async () => {
    const t = validate();
    if (t === null) return;
    setBusy(true);
    try {
      await onSubmit?.(t);
    } finally {
      setBusy(false);
    }
  }, [validate, onSubmit]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!busy) onCancel?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const InputEl = multiline ? 'textarea' : 'input';

  return createPortal(
    <div
      className={`modal-overlay confirm-dialog-overlay${nestedOverlay ? ' confirm-dialog-overlay--nested' : ''}`}
      role="presentation"
      onClick={() => !busy && onCancel?.()}
    >
      <div
        className="confirm-dialog-card prompt-dialog-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-dialog-icon-wrap confirm-dialog-icon--info" aria-hidden>
          <i className="fas fa-edit" />
        </div>
        <h2 id="prompt-dialog-title" className="confirm-dialog-title">
          {title}
        </h2>
        {message ? <p className="confirm-dialog-message">{message}</p> : null}
        <div className="prompt-dialog-field">
          <InputEl
            id="prompt-dialog-input"
            className="prompt-dialog-input"
            placeholder={placeholder}
            aria-label={placeholder || title}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError('');
            }}
            disabled={busy}
            rows={multiline ? 4 : undefined}
          />
          {minLength > 0 ? <p className="prompt-dialog-hint">Minimum {minLength} karakter.</p> : null}
          {error ? <p className="prompt-dialog-error">{error}</p> : null}
        </div>
        <div className="confirm-dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={() => !busy && onCancel?.()} disabled={busy}>
            {cancelLabel}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden /> Bekleyin…
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PromptDialog;
